import React, { lazy, Suspense } from 'react'
import { AuthRoute, NotFound, Route, Switch, Redirect, useLocation } from '@fs/zion-router'
import { useUser } from '@fs/zion-user'
import { useFeatureFlag } from '@fs/zion-flags'
import ReportsSkeleton from './pages/reports/ReportsSkeleton'
import OverviewSkeleton from './pages/overview/Skeleton'
import { useAppProvider } from './providers/AppProvider'
import useIsInmate from './hooks/useIsInmate'
import ReportsProvider from './pages/reports/ReportsProvider'
import HintsAuth from './components/auth/HintsAuth'
import HintsRoute from './components/auth/HintsRoute'
import ReportsAuth from './components/auth/ReportsAuth'
import PrisonReportsAuth from './components/auth/PrisonReportsAuth'

const CensusPage = lazy(() => import('./components/CensusPage'))
const MobileDeepLink = lazy(() => import('./components/MobileDeepLink'))
const Opportunities = lazy(() => import('./pages/opportunities'))
const Overview = lazy(() => import('./pages/overview'))
const Reports = lazy(() => import('./pages/reports'))
const Share = lazy(() => import('./pages/share'))
const Unauthorized = lazy(() => import('./pages/unauthorized'))
const VerifyPlaces = lazy(() => import('./pages/verify-places'))
const YourImpact = lazy(() => import('./pages/your-impact'))
const AdminConsole = lazy(() => import('./pages/admin'))
const LabsPage = lazy(() => import('./pages/labs'))
const OldRecordHints = lazy(() => import('./pages/record-hints/RecordHints'))
const RecordHints = lazy(() => import('./pages/record-hints/index'))
const Corrections = lazy(() => import('./pages/corrections/Corrections'))
const IndexingOverviewPage = lazy(() => import('./pages/indexing-overview/IndexingOverviewPage/IndexingOverviewPage'))

const Routes = ({ hasCensusPermission }) => {
  const { pathname } = useLocation()
  const { userLoading } = useUser()
  const newRecordHintsFF = useFeatureFlag('give_new_record_hints')
  const seoIndexingOverviewFlag = useFeatureFlag('platform_SEO_indexingOverview')
  const { accessFromPrison } = useAppProvider()
  const { isInmate, isLoading } = useIsInmate()

  const isReady = !isLoading && !userLoading

  if (!isReady) {
    return null
  }

  if ((isInmate || accessFromPrison) && !pathname.includes('/corrections')) {
    return <Redirect to="/corrections" />
  }

  return (
    <Switch>
      <Redirect exact from="/overview" to="/" />
      <Route exact path="/">
        <Suspense fallback={<OverviewSkeleton />}>
          <Overview />
        </Suspense>
      </Route>
      <Route path="/indexing">
        <Suspense fallback={null}>
          <IndexingOverviewPage />
        </Suspense>
      </Route>
      <Route exact path="/appstores" component={MobileDeepLink} />
      {seoIndexingOverviewFlag?.isOn && seoIndexingOverviewFlag?.config?.indexingOverviewPage && (
        <Route exact path="/seo-indexing/">
          <Suspense fallback={null}>
            <IndexingOverviewPage />
          </Suspense>
        </Route>
      )}
      <Redirect exact from="/my-opportunities" to="/opportunities" />
      <Route exact path="/share/:slug">
        <Suspense fallback={null}>
          <Share />
        </Suspense>
      </Route>
      {newRecordHintsFF.isOn ? (
        <HintsRoute path="/record-hints/:ark">
          {(routeProps) => (
            <Suspense fallback={null}>
              <RecordHints {...routeProps} />
            </Suspense>
          )}
        </HintsRoute>
      ) : (
        <HintsAuth exact path="/record-hints">
          <Suspense fallback={null}>
            <OldRecordHints />
          </Suspense>
        </HintsAuth>
      )}
      <Route exact path="/opportunities">
        <Suspense fallback={null}>
          <Opportunities />
        </Suspense>
      </Route>
      <AuthRoute exact path="/your-impact">
        <Suspense fallback={null}>
          <YourImpact data-testid="your-impact" />
        </Suspense>
      </AuthRoute>
      {hasCensusPermission && (
        <AuthRoute exact path="/1950">
          <Suspense fallback={null}>
            <CensusPage />
          </Suspense>
        </AuthRoute>
      )}
      <ReportsAuth exact path="/reports">
        <Suspense fallback={<ReportsSkeleton />}>
          <ReportsProvider>
            <Reports />
          </ReportsProvider>
        </Suspense>
      </ReportsAuth>
      <PrisonReportsAuth path="/prison-reports">
        <Suspense fallback={ReportsSkeleton}>
          <ReportsProvider>
            <Reports showPrisonReports />
          </ReportsProvider>
        </Suspense>
      </PrisonReportsAuth>
      <AuthRoute exact path="/verify-places">
        <VerifyPlaces />
      </AuthRoute>
      <AuthRoute path="/admin">
        <Suspense fallback={null}>
          <AdminConsole />
        </Suspense>
      </AuthRoute>
      <AuthRoute path="/labs">
        <Suspense fallback={null}>
          <LabsPage />
        </Suspense>
      </AuthRoute>
      <Route path="/corrections">
        <Suspense fallback={null}>
          <Corrections />
        </Suspense>
      </Route>
      <Route path="/unauthorized" component={Unauthorized} />
      <Route component={NotFound} />
    </Switch>
  )
}

export default Routes
